import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import styles from "./AutoJam.module.scss";
import { questions } from "./constants/autojamData";
import { TipIcon, DropdownIcon, CrossIcon } from "../../assets/icons";
import { ApiTable } from "@/shared/api/table/api-table";
import { useToastStore } from "@/shared/stores/toastStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { useTableStore } from "@/shared/stores/tableStore";
import { useNmsInfiniteQuery } from "../../hooks/useNmsInfiniteQuery";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { isDataPreparingError } from "@/shared/lib/apiErrorHandler";

interface NmsDataItem {
  article: string;
  name: string;
}

const SELECTION_LIMIT = 30;

export const SelectTable = () => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const hasInitialized = useRef(false);

  const { showToast } = useToastStore();
  const { pId, navigateToStage } = useServiceWorkflowStore();
  const { tableId, setSelectedNms } = useTableStore();
  const isEditExisted = !!tableId;

  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
    error,
  } = useNmsInfiniteQuery(pId, debouncedSearch);

  const isInitialLoading = isLoading && !data;
  const isRefetching = isFetching && !isFetchingNextPage && !isInitialLoading;

  const allItems = useMemo<NmsDataItem[]>(() => {
    return (data?.pages ?? []).flatMap((page) =>
      page.data.map((a) => ({
        article: String(a.nm_id),
        name: a.name,
      })),
    );
  }, [data]);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (!debouncedSearch && allItems.length > 0) {
      if (!pId) return;
      hasInitialized.current = true;
      const tableApi = new ApiTable();
      tableApi.getUsedNms(pId).then((res) => {
        const usedArticles = res.data?.data || [];
        const initial = new Set<string>(
          usedArticles.slice(0, SELECTION_LIMIT).map(String),
        );
        for (const item of allItems) {
          if (initial.size >= SELECTION_LIMIT) break;
          initial.add(item.article);
        }
        setSelectedItems(initial);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems.length]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleConfirm = async () => {
    const nmsAsNumbers = Array.from(selectedItems).map(Number);
    if (isEditExisted && tableId) {
      const api = new ApiTable();
      try {
        await api.setNms({ table_id: tableId, nms: nmsAsNumbers });
        showToast("Артикулы успешно обновлены", "success");
        navigateToStage("status");
      } catch {
        showToast("Ошибка при обновлении артикулов", "error");
      }
    } else {
      setSelectedNms(nmsAsNumbers);
      navigateToStage("stage_1");
    }
  };

  const toggleItemSelection = (article: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(article)) {
        next.delete(article);
      } else {
        if (next.size >= SELECTION_LIMIT) return prev;
        next.add(article);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const topItems = allItems.slice(0, SELECTION_LIMIT).map((i) => i.article);
    const allSelected = topItems.every((a) => selectedItems.has(a));
    if (allSelected && selectedItems.size === topItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(topItems));
    }
  };

  const isAllSelected =
    allItems.length > 0 &&
    allItems
      .slice(0, SELECTION_LIMIT)
      .every((i) => selectedItems.has(i.article));

  const renderTableBody = () => {
    if (isRefetching) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      );
    }

    if (allItems.length === 0) {
      return (
        <div className={styles.emptyState}>
          {debouncedSearch
            ? `По запросу «${debouncedSearch}» ничего не найдено`
            : "Список артикулов пуст"}
        </div>
      );
    }

    return (
      <>
        {allItems.map((item) => (
          <div
            className={styles.row}
            key={item.article}
            onClick={() => toggleItemSelection(item.article)}
            style={{ cursor: "pointer" }}
          >
            <div className={styles.checkboxCell}>
              <div
                className={`${styles.checkbox} ${
                  selectedItems.has(item.article) ? styles.selected : ""
                }`}
              >
                {selectedItems.has(item.article) && <TipIcon />}
              </div>
            </div>
            <div className={styles.dataCell}>
              <span className={styles.colored}>{item.article}</span>
            </div>
            <div className={styles.dataCell}>
              <span className={styles.dataCellName}>{item.name}</span>
            </div>
          </div>
        ))}

        <div ref={sentinelRef} style={{ height: 1 }} />

        {isFetchingNextPage && (
          <div className={styles.loadingContainer} style={{ minHeight: 60 }}>
            <div className={styles.spinner}></div>
          </div>
        )}
      </>
    );
  };

  if (isInitialLoading) {
    return (
      <section className={styles.card}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Загрузка артикулов...</p>
        </div>
      </section>
    );
  }

  if (isError) {
    const isPreparing = isDataPreparingError(error);
    return (
      <section className={styles.card}>
        <div className={styles.content} style={{ textAlign: "center" }}>
          <h2 className={styles.sectionMainTitle}>
            {isPreparing ? "Каталог ещё собирается" : "Ошибка"}
          </h2>
          <div className={styles.sectionText}>
            {isPreparing
              ? "Обычно это несколько минут — список обновится сам."
              : "Не удалось загрузить список артикулов."}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className={styles.card}>
        <div className={styles.content}>
          <h2 className={styles.sectionMainTitle}>Инструкция</h2>
          <div className={styles.sectionText}>
            Выберите до 30 артикулов. Сервис выгрузит их в таблицу Автоджема.
          </div>
          <div className={styles.searchInputWrapper}>
            <input
              type="text"
              placeholder="Поиск по названию или артикулу"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className={styles.clearIcon}
              >
                <CrossIcon color="#8C929C" />
              </button>
            )}
          </div>

          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <div className={styles.checkboxCell}>
                <div
                  className={`${styles.checkbox} ${isAllSelected ? styles.selected : ""}`}
                  onClick={toggleSelectAll}
                  style={{ cursor: "pointer" }}
                >
                  {isAllSelected && <TipIcon />}
                </div>
              </div>
              <div className={styles.headerCell}>Артикул</div>
              <div className={styles.headerCell}>Артикул продавца</div>
            </div>
            <div className={styles.tableBody}>{renderTableBody()}</div>
          </div>

          <button
            onClick={handleConfirm}
            className={styles.primaryButton}
            disabled={selectedItems.size === 0}
          >
            {isEditExisted ? "Сохранить изменения" : "Загрузить выбранное"} (
            {selectedItems.size})
          </button>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.content}>
          <div
            className={styles.toggleHeader}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <h2 className={styles.sectionTitle}>Ответы на вопросы</h2>
            <DropdownIcon
              className={`${styles.dropdownIcon} ${isOpen ? styles.open : ""}`}
            />
          </div>
          {isOpen && (
            <div className={styles.toggleContent}>
              <ol className={styles.list}>
                {questions.map(({ question, answer }, index) => (
                  <li key={index} className={styles.listItem}>
                    <span className={styles.listNumber}>{index + 1}</span>
                    <div className={styles.listContent}>
                      <span className={styles.listItemTitle}>{question}</span>
                      <div className={styles.listItemText}>{answer}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
